import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  pulse?: boolean;
  dot?: boolean;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-600 border-gray-200',
  primary: 'bg-primary-50 text-primary border-primary/20',
  success: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  warning: 'bg-amber-50 text-amber-600 border-amber-200',
  danger: 'bg-red-50 text-red-600 border-red-200',
  info: 'bg-blue-50 text-blue-600 border-blue-200',
  outline: 'bg-transparent text-gray-600 border-gray-300',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-xs px-2 py-1',
  lg: 'text-sm px-2.5 py-1',
};

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-gray-400',
  primary: 'bg-primary',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-blue-500',
  outline: 'bg-gray-400',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', pulse = false, dot = false, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 font-medium rounded-full border',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {dot && (
          <span className="relative flex h-2 w-2">
            {pulse && (
              <span
                className={cn(
                  'absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping',
                  dotColors[variant]
                )}
              />
            )}
            <span className={cn('relative inline-flex rounded-full h-2 w-2', dotColors[variant])} />
          </span>
        )}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Status Badge - specifically for node/service status
export type StatusType = 'active' | 'warning' | 'offline' | 'unknown';

interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: StatusType;
  size?: BadgeSize;
  showDot?: boolean;
  label?: string;
}

const statusConfig: Record<StatusType, { variant: BadgeVariant; label: string }> = {
  active: { variant: 'success', label: 'Active' },
  warning: { variant: 'warning', label: 'Warning' },
  offline: { variant: 'danger', label: 'Offline' },
  unknown: { variant: 'default', label: 'Unknown' },
};

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, size = 'md', showDot = true, label, className, ...props }, ref) => {
    const config = statusConfig[status];
    return (
      <Badge
        ref={ref}
        variant={config.variant}
        size={size}
        dot={showDot}
        pulse={status === 'active'}
        className={className}
        {...props}
      >
        {label || config.label}
      </Badge>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

// Latency Badge - specifically for latency display
export type LatencyQuality = 'excellent' | 'good' | 'fair' | 'poor';

interface LatencyBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  latency: number; // in milliseconds
  size?: BadgeSize;
}

const getLatencyQuality = (latency: number): { quality: LatencyQuality; variant: BadgeVariant } => {
  if (latency < 50) return { quality: 'excellent', variant: 'success' };
  if (latency < 100) return { quality: 'good', variant: 'success' };
  if (latency < 150) return { quality: 'fair', variant: 'warning' };
  return { quality: 'poor', variant: 'danger' };
};

export const LatencyBadge = forwardRef<HTMLSpanElement, LatencyBadgeProps>(
  ({ latency, size = 'md', className, ...props }, ref) => {
    const { variant } = getLatencyQuality(latency);
    return (
      <Badge ref={ref} variant={variant} size={size} className={cn('font-mono', className)} {...props}>
        {latency.toFixed(1)}ms
      </Badge>
    );
  }
);

LatencyBadge.displayName = 'LatencyBadge';

export default Badge;
